import { GrammarPointList } from "@/components/books/GrammarPointList";
import { useBookLessonContent } from "@/lib/hooks/bookLessons/useBookLessons";
import { type BookSection, useBookSections } from "@/lib/hooks/bookLessons/useBookSections";
import {
  type ContextLines,
  type LessonDataSource,
  type PartLines,
  countsWithEdits,
  editedLines,
  editedPartLines,
} from "@ai-teach/utils";
import { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import { type BookScope, BookScopePicker } from "./BookScopePicker";
import { EditableLines } from "./EditableLines";
import { DATA_SOURCES, DATA_SOURCE_ORDER, dataSourceCounts } from "./countRules";
import { type LessonRows, grammarPointsFor, itemsFor, partItems } from "./lessonContentItems";
import type { BookLessonRef } from "./types";

const SHOWN_LINES = 25;

/** What a row holds, so the author can check the content before drawing a section from it. */
function Lines({
  lines,
  empty,
  note,
}: { lines: string[]; empty: string; note?: string | undefined }) {
  if (lines.length === 0) {
    return <p className="text-xs text-gray-400 italic">{empty}</p>;
  }

  const hidden = lines.length - SHOWN_LINES;

  return (
    <div className="space-y-1">
      <ul className="space-y-0.5">
        {lines.slice(0, SHOWN_LINES).map((line, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a book may print one line twice
          <li key={`line-${index}`} className="text-xs text-gray-600 dark:text-gray-300">
            {line}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <p className="text-[11px] text-gray-400">
          and {hidden} more line{hidden === 1 ? "" : "s"}
        </p>
      )}
      {note && <p className="text-[11px] text-gray-400">{note}</p>}
    </div>
  );
}

/**
 * Grammar points are read here rather than edited: a point is its substitution table as much as its
 * name, and a list of lines can neither show one nor stand in for one. A section edited before this
 * can still be reset to the book's own points.
 */
function GrammarDetail({
  points,
  typed,
  empty,
  note,
  isEdited,
  onReset,
}: {
  points: LessonRows;
  typed: string[];
  empty: string;
  note?: string | undefined;
  isEdited: boolean;
  onReset: (() => void) | null;
}) {
  if (points.length === 0 && typed.length === 0) {
    return <p className="text-xs text-gray-400 italic">{empty}</p>;
  }

  return (
    <div className="space-y-2.5">
      <GrammarPointList points={points} />
      {typed.length > 0 && <Lines lines={typed} empty="" />}
      {note && <p className="text-[11px] text-gray-400">{note}</p>}
      {isEdited && onReset && (
        <button
          type="button"
          onClick={onReset}
          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-primary-500"
        >
          Reset
        </button>
      )}
    </div>
  );
}

/**
 * A part is shown as one lesson prints it — the lesson previewed here wherever it prints the part,
 * and a named other lesson where it does not, so a sample is never read as that lesson's.
 */
function describeSample(section: BookSection, previewId: string | null): string | undefined {
  if (!section.sampleLesson) return undefined;
  if (section.sampleLessonId === previewId) return undefined;
  return `This sample is from ${section.sampleLesson} — the lesson previewed here does not print this part.`;
}

function Row({
  label,
  count,
  checked,
  muted,
  edited,
  details,
  onToggle,
}: {
  label: string;
  count: string | null;
  checked: boolean;
  muted?: boolean;
  edited?: boolean;
  details: React.ReactNode;
  onToggle: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <li className="border-b border-gray-200 last:border-b-0 dark:border-gray-800">
      <div className="flex items-center">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5"
        >
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
              checked
                ? "border-accent-500 bg-accent-500 text-white"
                : "border-gray-300 text-transparent dark:border-gray-700"
            }`}
          >
            <FaCheck fontSize={10} />
          </span>
          <span className={`label truncate ${muted ? "text-error-500" : ""}`}>{label}</span>
          {edited && (
            <span className="shrink-0 rounded-full bg-accent-500/10 px-2 py-0.5 text-[10px] font-semibold text-accent-500">
              edited
            </span>
          )}
          {count && (
            <span
              className={`ml-auto shrink-0 text-sm tabular-nums ${muted ? "text-error-500" : "text-gray-500"}`}
            >
              {count}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} what ${label} holds`}
          className="shrink-0 px-2.5 py-2.5 text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
        >
          {open ? <HiChevronDown className="h-4 w-4" /> : <HiChevronRight className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-800/70">{details}</div>
      )}
    </li>
  );
}

/**
 * Which parts of the lesson one section may draw on; it reads what it checks and nothing more. A
 * part's count is the lessons printing it, since a lesson printing none of the checked ones writes
 * no slides here. Every row opens to show what it holds, so a source is checked having seen it.
 */
export function SourcePicker({
  book,
  bookScope,
  sampleLesson,
  lines,
  partLines,
  selected,
  scopes,
  subLists,
  onToggle,
  onScopesChange,
  onSubListsChange,
  onLinesChange,
  onPartLinesChange,
}: {
  book: BookLessonRef | null;
  /** The book whose parts are offered; without a series there is none to read yet. */
  bookScope: BookScope;
  /** The lesson to read lines from where a batch runs over many and none of them is the target. */
  sampleLesson: { id: string; printedLabel: string } | null;
  lines: ContextLines[];
  partLines: PartLines[];
  selected: LessonDataSource[];
  scopes: string[];
  subLists: string[];
  onToggle: (source: LessonDataSource) => void;
  onScopesChange: (scopes: string[]) => void;
  onSubListsChange: (subLists: string[]) => void;
  /** Left out where the lines cannot be edited for the run — a batch has no one lesson to edit. */
  onLinesChange?: (source: LessonDataSource, lines: string[] | null) => void;
  onPartLinesChange?: (section: string, lines: string[] | null) => void;
}) {
  const previewId = book?.id ?? sampleLesson?.id ?? null;
  // Only which parts to list — a section stores part names, so a book named here is never saved.
  const [pickedScope, setPickedScope] = useState<BookScope | null>(null);
  const scope = bookScope.seriesSlug ? bookScope : (pickedScope ?? bookScope);
  const { sections, loading } = useBookSections(scope, previewId);
  const content = useBookLessonContent(previewId);
  // Counts stay with the target: a batch's lessons each hold their own, so there is no one number.
  const counts = book ? countsWithEdits(dataSourceCounts(book), lines) : null;
  // A part the book no longer prints — a preset built on another one — stays visible to be cleared.
  const orphans = scopes.filter((scope) => !sections.some((one) => one.name === scope));
  const order = new Map(sections.map((one, index) => [one.name, index]));
  // The run's lines are the target lesson's own, so a sampled batch and a loading lesson only read.
  const editLines = onLinesChange && book && !content.loading ? onLinesChange : null;
  // The book's own sub-lists of words, in the order it lists them. A sub-list checked on a lesson
  // that does not list it stays visible to be cleared, the way an orphaned part does.
  const ownSubLists = [
    ...new Set(content.rows.flatMap((row) => (row.subList ? [row.subList] : []))),
  ];
  const shownSubLists = [...ownSubLists, ...subLists.filter((one) => !ownSubLists.includes(one))];

  const toggleSubList = (name: string) => {
    onSubListsChange(
      subLists.includes(name) ? subLists.filter((one) => one !== name) : [...subLists, name]
    );
  };

  // Held in the book's own order, so the lines reach the generator the way the book prints them.
  const toggleScope = (name: string) => {
    const next = scopes.includes(name)
      ? scopes.filter((one) => one !== name)
      : [...scopes, name].sort(
          (a, b) =>
            (order.get(a) ?? Number.POSITIVE_INFINITY) - (order.get(b) ?? Number.POSITIVE_INFINITY)
        );
    onScopesChange(next);
  };

  // A batch is previewed through one of its lessons, so the lines are labelled as that lesson's.
  const sampleNote =
    !book && sampleLesson
      ? `Lines from ${sampleLesson.printedLabel}. Every lesson in the batch is read the same way.`
      : undefined;

  const explain = () => {
    if (!scope.seriesSlug) return "Name the book below and its parts are listed here.";
    if (loading) return "Reading this book's parts…";
    if (sections.length === 0) return "The lessons of this book print no part names.";
    return "A checked part is read whole, lines of every kind. The count is the lessons printing that part, of the lessons in this book — the rest write nothing for this section.";
  };

  return (
    <ul className="rounded-lg border border-gray-200 dark:border-gray-800">
      {DATA_SOURCE_ORDER.map((source) => {
        const edited = editedLines(lines, source);
        const own = previewId ? itemsFor(source, content.rows, content.pageLines) : [];
        const empty = previewId
          ? content.loading
            ? "Reading this lesson…"
            : `This lesson has no ${DATA_SOURCES[source].item}.`
          : "Pick a book lesson to see its lines.";

        return (
          <Row
            key={source}
            label={DATA_SOURCES[source].label}
            count={counts ? String(counts[source]) : null}
            checked={selected.includes(source)}
            edited={edited !== undefined}
            onToggle={() => onToggle(source)}
            details={
              source === "grammar" ? (
                <GrammarDetail
                  {...grammarPointsFor(content.rows, edited ?? own)}
                  empty={empty}
                  note={sampleNote}
                  isEdited={edited !== undefined}
                  onReset={editLines ? () => editLines(source, null) : null}
                />
              ) : editLines ? (
                <EditableLines
                  lines={edited ?? own}
                  isEdited={edited !== undefined}
                  onChange={(next) => editLines(source, next)}
                />
              ) : (
                <Lines lines={edited ?? own} empty={empty} note={sampleNote} />
              )
            }
          />
        );
      })}

      {/* The book lists these words under a sub-list of its own, so each is checked as its own
          source rather than counted with the main vocabulary. */}
      {shownSubLists.map((name) => {
        const own = content.rows.filter((row) => row.subList === name);
        const absent = own.length === 0;

        return (
          <Row
            key={`subList-${name}`}
            label={absent ? `${name} — not listed in this lesson` : name}
            count={String(own.length)}
            checked={subLists.includes(name)}
            muted={absent}
            onToggle={() => toggleSubList(name)}
            details={
              <Lines
                lines={own.map((row) => row.text)}
                empty="This lesson lists no words under this sub-list."
                note={sampleNote}
              />
            }
          />
        );
      })}
      <li className="border-b border-gray-200 bg-black/[0.02] px-3 py-1.5 dark:border-gray-800 dark:bg-white/[0.03]">
        <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Parts of the lesson
        </p>
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{explain()}</p>
        {!bookScope.seriesSlug && (
          <div className="mt-2 grid grid-cols-3 gap-2 pb-1">
            <BookScopePicker
              idPrefix="parts-scope"
              value={scope}
              onChange={(next) => setPickedScope(next)}
            />
          </div>
        )}
      </li>

      {sections.map((one) => {
        const edited = editedPartLines(partLines, one.name);
        // Edited against the lesson's own lines, not the capped sample the book sends: the author's
        // list stands in for the whole part, so what they see has to be all of it.
        const own = book ? partItems(one.name, content.pageLines) : [];
        const editPart =
          onPartLinesChange && !content.loading && own.length > 0 ? onPartLinesChange : null;

        return (
          <Row
            key={one.name}
            label={one.name}
            count={`${one.lessonCount}/${one.totalLessons}`}
            checked={scopes.includes(one.name)}
            edited={edited !== undefined}
            onToggle={() => toggleScope(one.name)}
            details={
              editPart ? (
                <EditableLines
                  lines={edited ?? own}
                  isEdited={edited !== undefined}
                  onChange={(next) => editPart(one.name, next)}
                />
              ) : (
                <Lines
                  lines={edited ?? one.sampleLines}
                  empty="This part prints no lines the generator can read."
                  note={describeSample(one, previewId)}
                />
              )
            }
          />
        );
      })}

      {orphans.map((scope) => (
        <Row
          key={scope}
          label={`${scope} — not printed in this book`}
          count="0"
          checked
          muted
          onToggle={() => toggleScope(scope)}
          details={
            <p className="text-xs text-gray-400 italic">
              Nothing to show: no lesson of this book prints it.
            </p>
          }
        />
      ))}
    </ul>
  );
}
